// src/components/ui/PrivateCelebrationModal.jsx

import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import { Checkbox } from './Checkbox';

// Form Options
const celebrationTypeOptions = [
    { value: 'milestone-birthday', label: 'Milestone Birthday' },
    { value: 'anniversary', label: 'Anniversary Celebration' },
    { value: 'engagement-party', label: 'Engagement Party' },
    { value: 'graduation-party', label: 'Graduation Party' },
    { value: 'baby-shower', label: 'Baby Shower / Gender Reveal' },
    { value: 'family-reunion', label: 'Family Reunion' },
    { value: 'other', label: 'Other Special Occasion' },
];

const guestCountOptions = [
    { value: 'intimate-under-25', label: 'Intimate (Under 25)' },
    { value: 'small-25-50', label: 'Small (25-50 Guests)' },
    { value: 'medium-51-100', label: 'Medium (51-100 Guests)' },
    { value: 'large-100+', label: 'Large (100+ Guests)' },
];

const serviceNeedsOptions = [
 
    { id: 'decor-styling', label: 'Decor & Styling' },
  
 
    { id: 'venue-selection', label: 'Venue Selection' },
];

const PrivateCelebrationModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        celebrationType: '',
        guestCount: '',
        eventDate: '',
        needs: [],
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleCheckboxToggle = (needId) => {
        setFormData(prev => ({
            ...prev,
            needs: prev.needs.includes(needId)
                ? prev.needs.filter(id => id !== needId)
                : [...prev.needs, needId]
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Your name is required';
        if (!formData.phone.trim()) newErrors.phone = 'A phone number is required';
        if (!formData.celebrationType) newErrors.celebrationType = 'Please select a celebration type';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);

        const whatsappNumber = "+254727937010";
        const celebrationTypeLabel = celebrationTypeOptions.find(opt => opt.value === formData.celebrationType)?.label;
        const guestCountLabel = guestCountOptions.find(opt => opt.value === formData.guestCount)?.label;
        const selectedNeeds = formData.needs.map(needId => serviceNeedsOptions.find(n => n.id === needId)?.label).join(', ');

        let message = `*🎉 Private Celebration Inquiry 🎉*\n\n`;
        message += `*Client Name:* ${formData.name}\n`;
        message += `*Phone:* ${formData.phone}\n`;
        if(formData.email) message += `*Email:* ${formData.email}\n`;
        message += `\n*Event Details:*\n`;
        message += `  - *Celebration Type:* ${celebrationTypeLabel}\n`;
        if(formData.guestCount) message += `  - *Guest Count:* ${guestCountLabel}\n`;
        if(formData.eventDate) message += `  - *Desired Date:* ${formData.eventDate}\n`;
        if(selectedNeeds) message += `\n*Services Needed:*\n${selectedNeeds}\n`;
        if (formData.message) message += `\n*More Information:*\n${formData.message}`;

        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        
        setIsSubmitting(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="font-serif font-bold text-2xl text-gray-900">Plan Your Celebration</h2>
                            <p className="font-sans text-gray-500 mt-1">Let's create memories that will last a lifetime.</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose} className="-mt-2 -mr-2">
                            <Icon name="X" size={24} />
                        </Button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Your Name" required value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} error={errors.name} />
                        <Input label="Phone Number" type="tel" required value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} error={errors.phone} />
                    </div>
                    <div>
                         <Input label="Email Address (Optional)" type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select label="Type of Celebration" required options={celebrationTypeOptions} value={formData.celebrationType} onChange={(value) => handleInputChange('celebrationType', value)} error={errors.celebrationType} />
                        <Select label="Estimated Guest Count" options={guestCountOptions} value={formData.guestCount} onChange={(value) => handleInputChange('guestCount', value)} />
                    </div>
                    <div>
                        <Input label="Desired Event Date (Optional)" type="date" value={formData.eventDate} onChange={(e) => handleInputChange('eventDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-3 block">What help do you need?</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {serviceNeedsOptions.map((need) => (
                                <Checkbox key={need.id} label={need.label} checked={formData.needs.includes(need.id)} onChange={() => handleCheckboxToggle(need.id)} />
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-2 block">Tell us about your ideas (Optional)</label>
                        <textarea value={formData.message} onChange={(e) => handleInputChange('message', e.target.value)} placeholder="What are you celebrating? Any themes or styles you love?" rows={3} className="w-full p-3 border border-gray-300 rounded-lg font-sans bg-white focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                        <Button type="submit" variant="primary" loading={isSubmitting} fullWidth size="lg">
                            {isSubmitting ? 'Sending...' : 'Start Planning My Celebration'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PrivateCelebrationModal;